import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PaidSummary } from '../../lib/api';
import { fmtMoney } from '../../lib/currency';
import { POPUP_BLOCKED } from '@matix/receipt';
import { useUiStore, toDateStr } from '../../lib/uiStore';
import { useAuthStore } from '../../lib/authStore';
import { useLang, localName } from '../../lib/lang';
import { useErrMsg } from '../../lib/errors';
import { printSaleInvoice } from '../../lib/printInvoice';
import type { Customer, CreateSessionBody, UpdateSessionBody } from '../../lib/types';
import { isoForDateMinutes, localMinutesOfDay, minutesToLabel, prettyAuditValue, shortDateTime } from '../../lib/time';
import { ServicePicker, type PickedService } from './ServicePicker';
import { StatusSelect } from './StatusSelect';
import { PaymentDialog } from './PaymentDialog';
import { DepositDialog, StatusBadge } from './DepositDialog';

const AUDIT_COLOR: Record<string, string> = {
  create: '#16a34a', update: '#3b82f6', reschedule: '#6366f1', status_change: '#f59e0b', convert_pos: '#10b981', delete: '#ef4444',
  payment_recorded: '#0ea5e9', payment_link_sent: '#0ea5e9', payment_link_resent: '#0ea5e9', payment_cancelled: '#94a3b8',
};
const AUDIT_TEXT: Record<string, { en: string; ar: string }> = {
  create: { en: 'create', ar: 'إنشاء' }, update: { en: 'update', ar: 'تعديل' }, reschedule: { en: 'reschedule', ar: 'إعادة جدولة' },
  status_change: { en: 'status change', ar: 'تغيير الحالة' }, convert_pos: { en: 'convert pos', ar: 'تحويل لفاتورة' }, delete: { en: 'delete', ar: 'حذف' },
  payment_recorded: { en: 'deposit recorded', ar: 'تسجيل عربون' }, payment_link_sent: { en: 'payment link sent', ar: 'إرسال رابط دفع' },
  payment_link_resent: { en: 'payment link re-sent', ar: 'إعادة إرسال رابط دفع' }, payment_cancelled: { en: 'payment cancelled', ar: 'إلغاء دفعة' },
};

const minutesToHHmm = (m: number): string => minutesToLabel(m, false);
const hhmmToMinutes = (v: string): number => {
  const [h = '0', m = '0'] = v.split(':');
  return Number(h) * 60 + Number(m);
};

export function BookingDialog(): JSX.Element | null {
  const booking = useUiStore((s) => s.booking);
  const branchId = useUiStore((s) => s.branchId);
  const calDate = useUiStore((s) => s.date);
  const close = useUiStore((s) => s.closeBooking);
  const session = useAuthStore((s) => s.session);
  const { t, lang } = useLang();
  const errMsg = useErrMsg();
  const qc = useQueryClient();

  const isEdit = booking?.sessionId != null;
  const statusesQ = useQuery({ queryKey: ['statuses'], queryFn: api.statuses });
  const resourcesQ = useQuery({ queryKey: ['resources', branchId], queryFn: () => api.resources(branchId!), enabled: branchId != null });
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: api.tags });
  const sessionQ = useQuery({ queryKey: ['session', booking?.sessionId], queryFn: () => api.getSession(booking!.sessionId!), enabled: isEdit });
  const auditQ = useQuery({ queryKey: ['audit-session', booking?.sessionId], queryFn: () => api.audit({ sessionId: booking!.sessionId!, limit: 15 }), enabled: isEdit });

  // ── form state ──
  const [employeeId, setEmployeeId] = useState<number | null>(booking?.prefill?.employeeId ?? null);
  const [dateStr, setDateStr] = useState(calDate);
  const [startMin, setStartMin] = useState<number>(booking?.prefill?.start ? localMinutesOfDay(booking.prefill.start) : 10 * 60);
  const [durationMin, setDurationMin] = useState<number>(30);
  const [durationTouched, setDurationTouched] = useState(false);
  const [statusId, setStatusId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [color, setColor] = useState('');
  const [services, setServices] = useState<Map<number, PickedService>>(new Map());
  const [tagIds, setTagIds] = useState<Set<number>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);

  // customer
  const [custMode, setCustMode] = useState<'existing' | 'new'>('existing');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [custSearch, setCustSearch] = useState('');
  const custResultsQ = useQuery({
    queryKey: ['cust-search', custSearch],
    queryFn: () => api.searchCustomers(custSearch),
    enabled: custMode === 'existing' && custSearch.trim().length >= 2,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Printing state for the invoice created by the payment dialog.
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  // The till's payment dialog, opened by "Pay & invoice".
  const [payOpen, setPayOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);

  // Hydrate defaults from the loaded session (edit) or default status (create).
  useEffect(() => {
    if (isEdit && sessionQ.data) {
      const s = sessionQ.data;
      setEmployeeId(s.employee_id);
      setDateStr(toDateStr(new Date(s.start_at)));
      setStartMin(localMinutesOfDay(s.start_at));
      setDurationMin(s.duration_minutes);
      setDurationTouched(true);
      setStatusId(s.status_id);
      setNotes(s.notes ?? '');
      setInternalNotes(s.internal_notes ?? '');
      setColor(s.color ?? '');
      setServices(new Map(s.services.map((x) => [x.item_id, { item_id: x.item_id, name: x.item_name ?? String(x.item_id), price: x.unit_price, duration_minutes: x.duration_minutes }])));
      setTagIds(new Set(s.tag_ids));
      if (s.customer_id) {
        setCustMode('existing');
        setCustomer({ id: s.customer_id, customer_type: s.customer_type, name: s.customer_name, name_ar: null, phone: s.customer_phone, email: null });
      } else if (s.customer_name) {
        setCustMode('new');
        setNewName(s.customer_name);
        setNewPhone(s.customer_phone ?? '');
      }
    }
  }, [isEdit, sessionQ.data]);

  useEffect(() => {
    if (!isEdit && statusId == null && statusesQ.data) {
      const def = statusesQ.data.find((x) => x.is_default) ?? statusesQ.data[0];
      if (def) setStatusId(def.id);
    }
  }, [isEdit, statusId, statusesQ.data]);

  const serviceList = useMemo(() => [...services.values()], [services]);
  const serviceDuration = useMemo(() => serviceList.reduce((a, s) => a + s.duration_minutes, 0), [serviceList]);
  const servicesTotal = useMemo(() => serviceList.reduce((a, s) => a + s.price, 0), [serviceList]);

  // Auto-fill duration from services until the user overrides it.
  useEffect(() => {
    if (!durationTouched && serviceDuration > 0) setDurationMin(serviceDuration);
  }, [serviceDuration, durationTouched]);

  const toggleService = (svc: PickedService): void => {
    setServices((prev) => {
      const next = new Map(prev);
      if (next.has(svc.item_id)) next.delete(svc.item_id);
      else next.set(svc.item_id, svc);
      return next;
    });
  };

  const currentStatus = statusesQ.data?.find((s) => s.id === statusId);
  const actionStatuses = useMemo(() => {
    if (!statusesQ.data || !currentStatus) return [];
    const allowed = currentStatus.allowed_transitions;
    const pool = allowed.length > 0 ? statusesQ.data.filter((s) => allowed.includes(s.id)) : statusesQ.data.filter((s) => s.id !== currentStatus.id);
    return pool;
  }, [statusesQ.data, currentStatus]);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['calendar'] });
    if (booking?.sessionId) {
      void qc.invalidateQueries({ queryKey: ['session', booking.sessionId] });
      void qc.invalidateQueries({ queryKey: ['audit-session', booking.sessionId] });
    }
  };

  const buildCustomer = (): CreateSessionBody['customer'] => {
    if (custMode === 'existing' && customer) return { customer_type: 'cash', customer_id: customer.id };
    return { customer_type: 'cash', name: newName.trim(), phone: newPhone.trim() || undefined };
  };

  const canSave = branchId != null && (custMode === 'existing' ? !!customer : newName.trim().length > 0);

  /** The edit body for whatever the form is currently showing. */
  const buildUpdateBody = (): UpdateSessionBody => ({
    employee_id: employeeId,
    customer: buildCustomer(),
    start_at: isoForDateMinutes(dateStr, startMin),
    duration_minutes: durationMin,
    status_id: statusId ?? undefined,
    color: color || null,
    notes: notes || null,
    internal_notes: internalNotes || null,
    services: serviceList.map((s) => ({ item_id: s.item_id, qty: 1 })),
    tag_ids: [...tagIds],
  });

  const save = async (): Promise<void> => {
    if (branchId == null) return;
    setSaving(true);
    setError(null);
    try {
      const startIso = isoForDateMinutes(dateStr, startMin);
      const svc = serviceList.map((s) => ({ item_id: s.item_id, qty: 1 }));
      if (isEdit && booking?.sessionId) {
        await api.updateSession(booking.sessionId, buildUpdateBody());
      } else {
        const body: CreateSessionBody = {
          branch_id: branchId,
          employee_id: employeeId,
          customer: buildCustomer(),
          start_at: startIso,
          duration_minutes: durationMin,
          status_id: statusId ?? undefined,
          color: color || null,
          notes: notes || null,
          internal_notes: internalNotes || null,
          services: svc,
          tag_ids: [...tagIds],
        };
        await api.createSession(body);
      }
      invalidate();
      close();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doStatus = async (id: number): Promise<void> => {
    if (!booking?.sessionId) return;
    setSaving(true);
    setError(null);
    try {
      await api.changeStatus(booking.sessionId, { status_id: id });
      setStatusId(id);
      invalidate();
      void sessionQ.refetch();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Print the converted sale with the cashier's receipt pipeline. Kept separate
   * from `doConvert` so the "Print again" button can retry without re-posting
   * the conversion (which the backend would reject as ALREADY_CONVERTED).
   */
  const doPrint = async (receiptId: number): Promise<void> => {
    setPrinting(true);
    setPrintError(null);
    try {
      await printSaleInvoice({
        receiptId,
        fallbackCashier: { id: session?.user_id ?? 0, name: session?.username ?? null },
      });
    } catch (e) {
      setPrintError(
        (e as Error)?.message === POPUP_BLOCKED
          ? t({ en: 'Printing was blocked — allow pop-ups, then print again.', ar: 'تم حظر الطباعة — اسمح بالنوافذ المنبثقة ثم أعد الطباعة.' })
          : t({ en: 'The sale was created but the receipt didn’t print.', ar: 'تم إنشاء الفاتورة لكن تعذّرت طباعة الإيصال.' }),
      );
    } finally {
      setPrinting(false);
    }
  };

  /**
   * What the SERVER holds for this appointment, priced the way the conversion
   * will price it. The payment dialog reads this rather than the form's own
   * `serviceList`, so the total on the payment screen is the total on the
   * receipt even if the two have drifted.
   */
  const savedServices = useMemo(
    () =>
      (sessionQ.data?.services ?? []).map((s) => ({
        name: s.item_name ?? String(s.item_id),
        qty: s.qty,
        unit_price: s.unit_price,
      })),
    [sessionQ.data],
  );

  /**
   * Open the payment dialog.
   *
   * Unsaved edits are persisted first. Money is taken against what the server
   * holds, so a service added but not yet saved would otherwise be left off the
   * invoice while sitting on the screen — the receptionist would charge for a
   * basket the customer can see and the receipt does not contain.
   */
  const openPay = async (): Promise<void> => {
    if (!booking?.sessionId) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateSession(booking.sessionId, buildUpdateBody());
      await sessionQ.refetch();
      invalidate();
      setPayOpen(true);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  /**
   * The sale posted from the payment dialog.
   *
   * Converting writes a real POS receipt — GL voucher, stock receipt, shift
   * cash — and cannot be undone from here, which is exactly why the money is
   * now taken in the till's own payment dialog rather than behind a `confirm()`
   * that booked everything on Cash. By the time this runs the receipt exists.
   */
  const onPaid = (r: PaidSummary): void => {
    setPayOpen(false);
    setError(null);
    setFlash(
      r.change > 0
        ? t({
            en: `Sale created — receipt #${r.entry_no} (${fmtMoney(r.net_value)}) · change ${fmtMoney(r.change)}`,
            ar: `تم إنشاء فاتورة — رقم ${r.entry_no} (${fmtMoney(r.net_value)}) · الباقي ${fmtMoney(r.change)}`,
          })
        : t({
            en: `Sale created — receipt #${r.entry_no} (${fmtMoney(r.net_value)})`,
            ar: `تم إنشاء فاتورة — رقم ${r.entry_no} (${fmtMoney(r.net_value)})`,
          }),
    );
    invalidate();
    void sessionQ.refetch();
    // The sale is already committed — a print failure must never read as a
    // failed conversion, so it surfaces as its own notice. The footer's Print
    // button (shown once the refetch lands) is the retry.
    void doPrint(r.order_id);
  };

  const doDelete = async (): Promise<void> => {
    if (!booking?.sessionId) return;
    if (!confirm(t({ en: 'Delete this appointment?', ar: 'حذف هذا الموعد؟' }))) return;
    setSaving(true);
    try {
      await api.deleteSession(booking.sessionId);
      invalidate();
      close();
    } catch (e) {
      setError(errMsg(e));
      setSaving(false);
    }
  };

  if (!booking) return null;

  /*
   * On a phone this is a full-screen sheet with its own header / scrolling
   * body / footer; from sm it is the centred card it always was.
   *
   * The mobile split is not cosmetic. The backdrop used to be
   * `items-stretch`, which pins the card's BOX to the viewport height while
   * its content keeps going — so the white background stopped at the fold and
   * the activity log, the status buttons and the footer were painted straight
   * onto the translucent backdrop with the calendar showing through. Giving
   * the card `h-full` and scrolling the body inside it fixes that, and has the
   * better side effect that Save is always on screen instead of at the end of
   * a long scroll past the audit table.
   */
  return (
    <div
      className="fixed inset-0 z-40 flex justify-center overflow-hidden bg-black/40 sm:items-start sm:overflow-auto sm:p-6 md:p-8"
      onClick={close}
    >
      <div
        className="appt-dialog appt-card flex h-full w-full max-w-4xl flex-col rounded-none border-0 sm:my-auto sm:h-auto sm:rounded-2xl sm:border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — pinned on mobile, ordinary on desktop. */}
        <div className="flex flex-none items-start justify-between gap-2 border-b border-slate-200 px-4 py-3 sm:border-0 sm:px-6 sm:pb-0 sm:pt-6 dark:border-slate-700 dark:sm:border-0">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-bold text-slate-800 sm:text-xl dark:text-slate-100">{isEdit ? t({ en: 'Edit appointment', ar: 'تعديل الموعد' }) : t({ en: 'New appointment', ar: 'موعد جديد' })}</h2>
            {currentStatus && <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: currentStatus.color }}>{localName(lang, currentStatus.name, currentStatus.name_ar)}</span>}
          </div>
          <button className="-me-1 flex h-10 w-10 flex-none items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800" onClick={close} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>✕</button>
        </div>

        {/* Body. Scrolls itself on mobile; on sm+ it grows and the backdrop
            scrolls, exactly as it did before. */}
        <div className="appt-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:overflow-y-visible sm:px-6">
        {error && <div role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-base text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</div>}
        {flash && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-base text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            <span>{flash}</span>
            {printing && <span className="text-sm opacity-70">· {t({ en: 'printing…', ar: 'جارٍ الطباعة…' })}</span>}
          </div>
        )}
        {printError && <div role="alert" className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-base text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">{printError}</div>}

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Customer */}
          <div className="sm:col-span-2">
            <div className="mb-1 flex items-center justify-between">
              <label className="appt-label mb-0">{t({ en: 'Customer', ar: 'العميل' })}</label>
              <button className="text-sm font-semibold text-brand hover:underline" onClick={() => { setCustMode(custMode === 'existing' ? 'new' : 'existing'); setCustomer(null); }}>
                {custMode === 'existing' ? t({ en: '+ New customer', ar: '+ عميل جديد' }) : t({ en: 'Search existing', ar: 'بحث عن عميل' })}
              </button>
            </div>
            {custMode === 'existing' ? (
              customer ? (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700">
                  <span className="text-base font-medium text-slate-800 dark:text-slate-100">{customer.name} {customer.phone && <span className="text-slate-400">· {customer.phone}</span>}</span>
                  <button className="text-sm text-slate-400 hover:text-red-500" onClick={() => setCustomer(null)}>{t({ en: 'change', ar: 'تغيير' })}</button>
                </div>
              ) : (
                <div className="relative">
                  <input className="appt-input" placeholder={t({ en: 'Search by name or phone…', ar: 'ابحث بالاسم أو الهاتف…' })} value={custSearch} onChange={(e) => setCustSearch(e.target.value)} />
                  {custSearch.trim().length >= 2 && (custResultsQ.data?.length ?? 0) > 0 && (
                    <div className="absolute z-10 mt-1 max-h-52 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg appt-scroll dark:border-slate-700 dark:bg-slate-800">
                      {custResultsQ.data!.map((c) => (
                        <button key={c.id} className="flex w-full items-center justify-between px-3 py-2 text-start text-base hover:bg-slate-50 dark:hover:bg-slate-700" onClick={() => { setCustomer(c); setCustSearch(''); }}>
                          <span className="font-medium text-slate-800 dark:text-slate-100">{c.name}</span>
                          <span className="text-sm text-slate-400">{c.phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input className="appt-input" placeholder={t({ en: 'Name', ar: 'الاسم' })} value={newName} onChange={(e) => setNewName(e.target.value)} />
                <input className="appt-input" type="tel" inputMode="tel" dir="ltr" placeholder={t({ en: 'Phone', ar: 'الهاتف' })} value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
              </div>
            )}
          </div>

          {/* Employee */}
          <div>
            <label className="appt-label">{t({ en: 'Employee', ar: 'الموظف' })}</label>
            <select className="appt-input" value={employeeId ?? ''} onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">{t({ en: '— Unassigned —', ar: '— غير معيّن —' })}</option>
              {(resourcesQ.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          {/* Status (create) */}
          <div>
            <label className="appt-label">{t({ en: 'Status', ar: 'الحالة' })}</label>
            <StatusSelect statuses={statusesQ.data ?? []} value={statusId} onChange={setStatusId} placeholder={t({ en: 'Select status', ar: 'اختر الحالة' })} />
          </div>

          {/* Date/time/duration */}
          <div>
            <label className="appt-label">{t({ en: 'Date', ar: 'التاريخ' })}</label>
            <input type="date" className="appt-input" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="appt-label">{t({ en: 'Start', ar: 'البداية' })}</label>
              <input type="time" className="appt-input" value={minutesToHHmm(startMin)} onChange={(e) => setStartMin(hhmmToMinutes(e.target.value))} />
            </div>
            <div>
              <label className="appt-label">{t({ en: 'Duration (min)', ar: 'المدة (دقيقة)' })}</label>
              <input type="number" min={5} step={5} className="appt-input" value={durationMin} onChange={(e) => { setDurationTouched(true); setDurationMin(Number(e.target.value)); }} />
            </div>
          </div>

          {/* Services */}
          <div className="sm:col-span-2">
            <div className="mb-1 flex items-center justify-between">
              <label className="appt-label mb-0">{t({ en: 'Services', ar: 'الخدمات' })}</label>
              <button className="text-sm font-semibold text-brand hover:underline" onClick={() => setPickerOpen(true)}>{t({ en: '+ Add services', ar: '+ إضافة خدمات' })}</button>
            </div>
            {serviceList.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 px-3 py-3 text-center text-sm text-slate-400 dark:border-slate-700">{t({ en: 'No services yet', ar: 'لا توجد خدمات بعد' })}</p>
            ) : (
              <div className="space-y-1">
                {serviceList.map((s) => (
                  <div key={s.item_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-base dark:border-slate-700">
                    <span className="font-medium text-slate-800 dark:text-slate-100">{s.name}</span>
                    <span className="flex items-center gap-3 text-sm text-slate-400">
                      <span>{s.duration_minutes} {t({ en: 'min', ar: 'دقيقة' })} · {fmtMoney(s.price)}</span>
                      <button className="text-slate-400 hover:text-red-500" onClick={() => toggleService(s)}>✕</button>
                    </span>
                  </div>
                ))}
                <div className="flex justify-end gap-4 pt-1 text-sm font-semibold text-slate-500">
                  <span>{serviceDuration} {t({ en: 'min total', ar: 'دقيقة إجمالي' })}</span>
                  <span>{fmtMoney(servicesTotal)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          {(tagsQ.data?.length ?? 0) > 0 && (
            <div className="sm:col-span-2">
              <label className="appt-label">{t({ en: 'Tags', ar: 'الوسوم' })}</label>
              <div className="flex flex-wrap gap-1.5">
                {tagsQ.data!.map((tag) => {
                  const on = tagIds.has(tag.id);
                  return (
                    <button key={tag.id} onClick={() => setTagIds((prev) => { const n = new Set(prev); n.has(tag.id) ? n.delete(tag.id) : n.add(tag.id); return n; })}
                      className="rounded-full px-2.5 py-1 text-sm font-medium ring-1 transition"
                      style={{ background: on ? tag.color : 'transparent', color: on ? '#fff' : tag.color, borderColor: tag.color, boxShadow: `inset 0 0 0 1px ${tag.color}` }}>
                      {localName(lang, tag.name, tag.name_ar)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="appt-label">{t({ en: 'Notes', ar: 'ملاحظات' })}</label>
            <textarea className="appt-input min-h-[64px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <label className="appt-label">{t({ en: 'Internal notes', ar: 'ملاحظات داخلية' })}</label>
            <textarea className="appt-input min-h-[64px]" value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} />
          </div>
        </div>

        {/*
          Payments (edit). Total / deposit / paid / remaining, every deposit
          row with its method and status, and the button that collects one.
          Null payments = the tenant has no 058 table yet; the card is simply
          absent and everything else behaves as before.
        */}
        {isEdit && sessionQ.data?.payments && (() => {
          const pay = sessionQ.data.payments!;
          const depositRows = pay.payments.filter((p) => p.status !== 'cancelled' && p.status !== 'failed' && p.status !== 'expired');
          const depositTotal = depositRows.filter((p) => p.status === 'paid' || p.status === 'applied').reduce((a, p) => a + p.amount, 0);
          return (
            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-700">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{t({ en: 'Payments', ar: 'المدفوعات' })}</h3>
                {sessionQ.data.gl_receipt_id == null && !sessionQ.data.cancelled_at && pay.remaining - pay.pending > 0 && (
                  <button className="text-sm font-semibold text-brand hover:underline" onClick={() => setDepositOpen(true)}>
                    {t({ en: '+ Collect deposit', ar: '+ تحصيل عربون' })}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-xl border border-slate-200 p-3 text-sm sm:grid-cols-4 dark:border-slate-700">
                <Money label={t({ en: 'Total session amount', ar: 'إجمالي الجلسة' })} value={pay.total} />
                <Money label={t({ en: 'Deposit', ar: 'العربون' })} value={depositTotal + pay.online_deposit_available} />
                <Money label={t({ en: 'Paid', ar: 'المدفوع' })} value={pay.paid} tone="ok" />
                <Money label={t({ en: 'Remaining balance', ar: 'المبلغ المتبقي' })} value={pay.remaining} strong />
              </div>
              {pay.pending > 0 && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  {t({ en: `${fmtMoney(pay.pending)} is awaiting payment on a link sent to the customer.`, ar: `${fmtMoney(pay.pending)} بانتظار السداد عبر رابط أُرسل للعميل.` })}
                </p>
              )}
              {pay.payments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {pay.payments.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-700">
                      <span className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {p.method === 'tap_invoice'
                            ? t({ en: 'Tap payment link', ar: 'رابط دفع Tap' })
                            : localName(lang, p.payment_type_name ?? '', p.payment_type_name_ar) || t({ en: 'Paid at the desk', ar: 'مدفوع في الاستقبال' })}
                        </span>
                        <StatusBadge status={p.status} />
                        {p.paid_at && <span className="text-xs text-slate-400">{shortDateTime(p.paid_at)}</span>}
                        {!p.paid_at && p.notified_at && <span className="text-xs text-slate-400">{t({ en: 'sent', ar: 'أُرسل' })} {shortDateTime(p.notified_at)}{p.notified_to ? ` · ${p.notified_to}` : ''}</span>}
                        {p.loyalty_advance_points > 0 && (
                          <span className="text-xs text-slate-400">· {p.loyalty_advance_points} {t({ en: 'pts', ar: 'نقطة' })}</span>
                        )}
                      </span>
                      <span className="tabular-nums font-semibold text-slate-800 dark:text-slate-100">{fmtMoney(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Activity trail (edit) */}
        {isEdit && (auditQ.data?.data.length ?? 0) > 0 && (
          <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-700">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-400">{t({ en: 'Activity', ar: 'النشاط' })}</h3>
            {/* Fixed layout so the four columns keep their widths and only
                Details reflows — the old flex row truncated the value away.
                Those fixed widths add up to more than a phone is wide, so the
                wrapper scrolls horizontally as well as vertically. */}
            <div className="appt-scroll max-h-56 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full min-w-[520px] table-fixed border-collapse text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="w-32 px-2 py-1.5 text-start font-semibold">{t({ en: 'When', ar: 'الوقت' })}</th>
                    <th className="w-28 px-2 py-1.5 text-start font-semibold">{t({ en: 'Action', ar: 'الإجراء' })}</th>
                    <th className="px-2 py-1.5 text-start font-semibold">{t({ en: 'Details', ar: 'التفاصيل' })}</th>
                    <th className="w-28 px-2 py-1.5 text-start font-semibold">{t({ en: 'User', ar: 'المستخدم' })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {auditQ.data!.data.map((e) => (
                    <tr key={e.id} className="align-top">
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-500 dark:text-slate-400">{shortDateTime(e.created_at)}</td>
                      <td className="px-2 py-1.5">
                        <span className="font-semibold capitalize" style={{ color: AUDIT_COLOR[e.action] ?? '#64748b' }}>
                          {AUDIT_TEXT[e.action] ? t(AUDIT_TEXT[e.action]!) : e.action.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="break-words px-2 py-1.5 text-slate-600 dark:text-slate-300">
                        {e.field_name && <span className="font-medium">{e.field_name}: </span>}
                        {e.old_value != null && <span className="text-slate-400 line-through">{prettyAuditValue(e.old_value)}</span>}
                        {e.old_value != null && e.new_value != null && <span className="mx-1">→</span>}
                        {e.new_value != null && <span>{prettyAuditValue(e.new_value)}</span>}
                        {e.old_value == null && e.new_value == null && <span className="text-slate-400">—</span>}
                      </td>
                      <td className="break-words px-2 py-1.5 text-slate-500 dark:text-slate-400">{e.user_name ?? (e.user_id != null ? `#${e.user_id}` : '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Status action bar (edit) */}
        {isEdit && actionStatuses.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            {actionStatuses.map((s) => (
              <button key={s.id} disabled={saving} onClick={() => void doStatus(s.id)}
                className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white transition disabled:opacity-50" style={{ background: s.color }}>
                {localName(lang, s.name, s.name_ar)}
              </button>
            ))}
          </div>
        )}
        </div>

        {/*
          Footer — outside the scrolling body, so on a phone it stays on screen.

          On edit it holds up to five controls, which do not fit across a phone.
          They stack into two rows there — destructive/secondary actions above,
          Cancel + Save below and full width — and return to a single justified
          row from sm. Save is last in DOM order so it is the button under the
          thumb.
        */}
        <div className="appt-dialog-footer flex flex-none flex-col gap-2 border-t border-slate-200 px-4 pt-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:pt-4 dark:border-slate-700">
          <div className="flex flex-wrap items-center gap-2">
            {isEdit && (
              <>
                <button className="appt-btn-ghost text-red-600" disabled={saving} onClick={() => void doDelete()}>{t({ en: 'Delete', ar: 'حذف' })}</button>
                {sessionQ.data?.gl_receipt_id == null ? (
                  <button
                    className="appt-btn-ghost font-bold text-emerald-700 dark:text-emerald-300"
                    disabled={saving || serviceList.length === 0}
                    onClick={() => void openPay()}
                    title={
                      serviceList.length === 0
                        ? t({ en: 'Add a service first', ar: 'أضف خدمة أولاً' })
                        : t({ en: 'Take payment and post the sale invoice', ar: 'استلام الدفع وترحيل فاتورة المبيعات' })
                    }
                  >
                    {t({ en: 'Pay & invoice', ar: 'الدفع وإصدار الفاتورة' })}
                  </button>
                ) : (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="self-center text-sm font-medium text-emerald-600">
                      {t({ en: 'Sale Invoice', ar: 'فاتورة مبيعات' })} #{sessionQ.data?.gl_entry_no ?? '—'}
                    </span>
                    <button
                      className="appt-btn-ghost"
                      disabled={printing}
                      onClick={() => void doPrint(sessionQ.data!.gl_receipt_id!)}
                      title={t({ en: 'Print the sale invoice', ar: 'طباعة فاتورة المبيعات' })}
                    >
                      <PrinterIcon />
                      {printing ? t({ en: 'Printing…', ar: 'جارٍ الطباعة…' }) : t({ en: 'Print', ar: 'طباعة' })}
                    </button>
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button className="appt-btn-ghost flex-1 sm:flex-none" onClick={close}>{t({ en: 'Cancel', ar: 'إلغاء' })}</button>
            <button className="appt-btn-primary flex-1 sm:flex-none" disabled={!canSave || saving} onClick={() => void save()}>
              {saving ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : isEdit ? t({ en: 'Save changes', ar: 'حفظ التغييرات' }) : t({ en: 'Book appointment', ar: 'حجز الموعد' })}
            </button>
          </div>
        </div>
      </div>

      {pickerOpen && branchId != null && (
        <ServicePicker branchId={branchId} selected={services} onToggle={toggleService} onClose={() => setPickerOpen(false)} />
      )}

      {/*
        The till's payment flow. It prices from the SAVED session's services,
        not from the unsaved form state — the receipt has to match the
        appointment the server holds, and an edit the receptionist has not
        saved yet must not quietly end up on an invoice.
      */}
      {payOpen && booking?.sessionId != null && (
        <PaymentDialog
          sessionId={booking.sessionId}
          services={savedServices}
          customerId={sessionQ.data?.customer_id ?? null}
          customerName={sessionQ.data?.customer_name ?? null}
          deposit={sessionQ.data?.deposit ?? null}
          payments={sessionQ.data?.payments ?? null}
          onClose={() => setPayOpen(false)}
          onPaid={onPaid}
        />
      )}

      {/*
        Collect a deposit before the invoice. Every reply carries the fresh
        money picture, which is written straight into the session query so the
        card above updates without a refetch; the invalidate keeps the calendar
        and audit trail honest once the dialog closes.
      */}
      {depositOpen && booking?.sessionId != null && sessionQ.data?.payments && (
        <DepositDialog
          sessionId={booking.sessionId}
          summary={sessionQ.data.payments}
          customerName={sessionQ.data.customer_name}
          customerPhone={sessionQ.data.customer_phone}
          onChanged={(summary) => {
            qc.setQueryData(['session', booking.sessionId], (prev: typeof sessionQ.data) => (prev ? { ...prev, payments: summary } : prev));
          }}
          onClose={() => {
            setDepositOpen(false);
            invalidate();
            void sessionQ.refetch();
          }}
        />
      )}
    </div>
  );
}

function Money({ label, value, strong, tone }: { label: string; value: number; strong?: boolean; tone?: 'ok' }): JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`tabular-nums ${strong ? 'text-base font-bold text-slate-800 dark:text-slate-100' : tone === 'ok' ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'font-semibold text-slate-700 dark:text-slate-200'}`}>
        {fmtMoney(value)}
      </span>
    </div>
  );
}

function PrinterIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
      <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
      <path d="M6 14h12v7H6z" />
    </svg>
  );
}
